import { useState, useRef, useEffect } from 'react'
import { WaveformIcon, PlaneIcon } from '@/components/ui/icons'
import { TYPING, SAMPLE_QUERIES } from './constants'
import { FONT_STYLES } from '@/styles/fonts'

export default function ChatboxPrompt() {
  const [inputValue, setInputValue] = useState('')
  const [isUserInteracting, setIsUserInteracting] = useState(false)
  const [isFlyingOff, setIsFlyingOff] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const currentQueryIndexRef = useRef(0)
  const currentCharIndexRef = useRef(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isUserInteractingRef = useRef(false)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [inputValue])

  // Sync ref with state
  useEffect(() => {
    isUserInteractingRef.current = isUserInteracting
  }, [isUserInteracting])

  // Auto-typing effect
  useEffect(() => {
    if (isUserInteracting) {
      // Clear any ongoing typing when user interacts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setIsFlyingOff(false)
      setIsResetting(false)
      return
    }

    const typeText = () => {
      // Check again if user started interacting using ref
      if (isUserInteractingRef.current) return

      const currentQuery = SAMPLE_QUERIES[currentQueryIndexRef.current]

      // Typing characters
      if (currentCharIndexRef.current < currentQuery.length) {
        setIsResetting(false) // Reveal text when typing starts
        setInputValue(currentQuery.substring(0, currentCharIndexRef.current + 1))
        currentCharIndexRef.current++
        timeoutRef.current = setTimeout(typeText, TYPING.CHAR_DELAY_MS)
      } else {
        // Finished typing, wait then fly off
        timeoutRef.current = setTimeout(() => {
          if (!isUserInteractingRef.current) {
            setIsFlyingOff(true)
            // After fly-off animation completes, move to next query
            timeoutRef.current = setTimeout(() => {
              if (!isUserInteractingRef.current) {
                setIsResetting(true) // Hide while resetting position
                setIsFlyingOff(false)
                setInputValue('')
                currentQueryIndexRef.current = (currentQueryIndexRef.current + 1) % SAMPLE_QUERIES.length
                currentCharIndexRef.current = 0
                timeoutRef.current = setTimeout(typeText, TYPING.RESET_DELAY_MS)
              }
            }, TYPING.FLY_OFF_DURATION_MS)
          }
        }, TYPING.HOLD_DELAY_MS)
      }
    }

    // Reset state when starting
    currentQueryIndexRef.current = 0
    currentCharIndexRef.current = 0
    setInputValue('')
    setIsFlyingOff(false)
    setIsResetting(false)

    // Start typing after initial delay
    timeoutRef.current = setTimeout(typeText, TYPING.INITIAL_DELAY_MS)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isUserInteracting])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)
    setIsUserInteracting(true)
    // Clear auto-typing timeout when user interacts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleInputFocus = () => {
    setIsUserInteracting(true)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }

  const handleInputBlur = () => {
    // Resume auto-typing after a delay if input is empty
    const currentValue = inputValue
    setTimeout(() => {
      // Check if input is still empty (user might have typed something)
      if (!currentValue.trim() && !isUserInteractingRef.current) {
        setIsUserInteracting(false)
        setIsFlyingOff(false)
        setIsResetting(false)
        currentQueryIndexRef.current = 0
        currentCharIndexRef.current = 0
      }
    }, TYPING.BLUR_RESUME_DELAY_MS)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Query box disabled - not hooked up yet
    console.log('[ChatboxPrompt] Submit disabled - query box not hooked up yet')
  }

  return (
    <div className="w-full px-4 py-6 flex justify-center">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-4xl relative"
      >
        <div className="relative flex items-center gap-3 bg-[#2d2d2d]/80 backdrop-blur-lg rounded-2xl px-4 py-3.5 shadow-lg">
          {/* Text input area */}
          <div className="flex-1 relative min-h-[24px] flex items-center overflow-hidden">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Ask me anything about your trip..."
              className="w-full bg-transparent text-white resize-none outline-none leading-relaxed placeholder:text-white/50 tracking-wide"
              style={{
                fontSize: '1.25rem', // 25% larger than text-sm (1rem * 1.25)
                minHeight: '24px',
                maxHeight: '120px',
                caretColor: 'white',
                ...FONT_STYLES.alga,
                transform: isFlyingOff ? 'translateX(100%)' : 'translateX(0)',
                opacity: isFlyingOff || isResetting ? 0 : 1,
                transition: isFlyingOff ? `transform ${TYPING.FLY_OFF_DURATION_MS}ms ease-in, opacity ${TYPING.FLY_OFF_DURATION_MS}ms ease-in` : 'none',
              }}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e)
                }
              }}
            />
          </div>

          {/* Right side controls */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="w-8 h-8 rounded-full bg-transparent hover:bg-white/10 flex items-center justify-center transition-colors"
              aria-label="Voice input"
            >
              <WaveformIcon />
            </button>
            <button
              type="submit"
              disabled={true}
              className="w-8 h-8 rounded-full bg-white/10 opacity-50 cursor-not-allowed flex items-center justify-center"
              aria-label="Send (disabled)"
              title="Coming soon!"
            >
              <PlaneIcon />
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

